import { uniq } from 'lodash'
import { Page } from '../../../models/util.model'
import { SupplierOrdersListingKeys } from '../models/supplier-order-listing.model'
import { SupplierOrder } from '../models/supplier-order.entity'
import {
  SupplierOrderField,
  SupplierOrderSearchParams,
  SupplierOrderSortField,
} from '../models/supplier-order-search.model'
import {
  QueryStringFilter,
  QueryStringSort,
} from '../../../models/query-string.model'
import {
  applyComparisonOperator,
  applyDateRangeFilter,
  parseFilters,
} from '../../../libs/filter.lib'

export function parseSupplierOrderFilters(
  params: SupplierOrderSearchParams,
  filters: SupplierOrderSearchParams,
): SupplierOrderSearchParams {
  return parseFilters(params, filters, supplierOrderFilterParams)
}

/**
 * Parse supplier order page keys
 * @param page - the supplier order page
 * @returns the page updated
 */
export function parseSupplierOrderPageKeys(
  page: Page<SupplierOrder>,
): SupplierOrdersListingKeys {
  return page.data.reduce<SupplierOrdersListingKeys>(
    (acc, r) => parseSupplierOrderKeys(r, acc),
    {},
  )
}

/**
 * Parse supplier order keys
 * @param goodsReceive - the supplier order
 * @param keys - the keys already loaded
 */
function parseSupplierOrderKeys(
  goodsReceive: SupplierOrder,
  keys: SupplierOrdersListingKeys = {},
): SupplierOrdersListingKeys {
  const warehouseIds = uniq([
    ...(keys['warehouseIds'] || []),
    ...(goodsReceive.warehouseId ? [goodsReceive.warehouseId] : []),
  ])
  const supplierIds = uniq([
    ...(keys['supplierIds'] || []),
    ...(goodsReceive.supplierId ? [goodsReceive.supplierId] : []),
  ])

  return {
    warehouseIds,
    supplierIds,
  }
}

export function supplierOrderSortParams(
  params: SupplierOrderSearchParams,
  sort: QueryStringSort<SupplierOrderSortField>,
): SupplierOrderSearchParams {
  const searchParams: SupplierOrderSearchParams = {}

  switch (sort.field) {
    case 'createdAt':
      searchParams.sort = 'createdAt'
      searchParams.order = sort.order
      break
    case 'header.orderNumber':
      searchParams.sort = 'header.orderNumber'
      searchParams.order = sort.order
      break
    case 'header.date':
      searchParams.sort = 'header.date'
      searchParams.order = sort.order
      break
    case 'header.rifDate':
      searchParams.sort = 'header.rifDate'
      searchParams.order = sort.order
      break
    case 'header.deliveryDate':
      searchParams.sort = 'header.deliveryDate'
      searchParams.order = sort.order
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

export function supplierOrderFilterParams(
  params: SupplierOrderSearchParams,
  filter: QueryStringFilter<SupplierOrderField>,
): SupplierOrderSearchParams {
  const searchParams: SupplierOrderSearchParams = {}
  const value = Array.isArray(filter.value)
    ? filter.value.length
      ? filter.value[0]
      : undefined
    : filter.value

  switch (filter.field) {
    case '_id':
      if (filter.operator === '=') {
        searchParams._id = filter.value
      }
      break
    case 'createdAt':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'createdAt',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'createdAt',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'status':
      if (filter.value !== null) {
        searchParams['status'] = filter.value
      }
      break
    case 'warehouseId':
      if (filter.value !== null) {
        searchParams.warehouseId = filter.value
      }
      break
    case 'supplierId':
      if (filter.value !== null) {
        searchParams.supplierId = filter.value
      }
      break
    case 'isPartial':
      searchParams.isPartial = filter.value
      break
    case 'isExported':
      searchParams.isExported = filter.value
      break
    case 'productsCount':
      if (filter.operator === '=') {
        searchParams['productsCount'] = filter.value
      } else if (filter.operator === '<') {
        searchParams['productsCount:lt'] = filter.value
      } else if (filter.operator === '<=') {
        searchParams['productsCount:le'] = filter.value
      } else if (filter.operator === '>') {
        searchParams['productsCount:gt'] = filter.value
      } else if (filter.operator === '>=') {
        searchParams['productsCount:ge'] = filter.value
      }
      break
    case 'rowsCount':
      if (filter.operator === '=') {
        searchParams['rowsCount'] = filter.value
      } else if (filter.operator === '<') {
        searchParams['rowsCount:lt'] = filter.value
      } else if (filter.operator === '<=') {
        searchParams['rowsCount:le'] = filter.value
      } else if (filter.operator === '>') {
        searchParams['rowsCount:gt'] = filter.value
      } else if (filter.operator === '>=') {
        searchParams['rowsCount:ge'] = filter.value
      }
      break
    case 'header.orderNumber':
      if (filter.operator === '=') {
        searchParams['header.orderNumber'] = filter.value
      } else if (filter.operator === '<') {
        searchParams['header.orderNumber:lt'] = filter.value
      } else if (filter.operator === '<=') {
        searchParams['header.orderNumber:le'] = filter.value
      } else if (filter.operator === '>') {
        searchParams['header.orderNumber:gt'] = filter.value
      } else if (filter.operator === '>=') {
        searchParams['header.orderNumber:ge'] = filter.value
      }
      break
    case 'header.date':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.date',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.date',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.rifDate':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.rifDate',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.rifDate',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.rifOrder':
      if (filter.operator === '=') {
        searchParams['header.rifOrder'] = filter.value
      }
      break
    case 'header.deliveryDate':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.deliveryDate',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.deliveryDate',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.currency':
      if (filter.value !== null) {
        searchParams['header.currency'] = filter.value
      }
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}
